import emrSectionData from '../SCREENING_RECOMMENDATION_EMR_SECTION.json'

/**
 * Hook for managing screening recommendation subsections (create, edit, remove).
 * Simple state management for recommendation subsections.
 * Automatically adds subsection label from EMR section definition.
 *
 * @param {Function} setScreeningRecommendationSectionData - State setter function
 */
export default function useScreeningRecommendationScreenerActions({
  setScreeningRecommendationSectionData,
}) {
  /**
   * Creates a new recommendation subsection.
   * Generates unique orderNumber and adds to subSections array.
   * Automatically includes subsection label from EMR definition.
   *
   * @param {string} recommendationId - Type: 'THERAPY' | 'DIAGNOSTIC_ASSESSMENT' | 'DIETICIAN_CONSULTATION'
   */
  function createRecommendation(recommendationId) {
    setScreeningRecommendationSectionData((prev) => {
      const currentSubSections = prev?.subSections || []

      // Generate unique order number (max + 1, or 0 if empty)
      const maxOrderNumber = currentSubSections.reduce(
        (max, section) => Math.max(max, section?.orderNumber || 0),
        -1,
      )

      // Find subsection definition to get label
      const subSectionDef = emrSectionData.emrSectionResponse.section.subSections.find(
        (s) => s.code === recommendationId,
      )

      const newSubSection = {
        subSectionCode: recommendationId,
        subSectionLabel: subSectionDef?.title || '', // Add subsection label
        orderNumber: maxOrderNumber + 1,
        values: {},
      }

      return {
        ...prev,
        subSections: [...currentSubSections, newSubSection],
      }
    })
  }

  /**
   * Removes a specific recommendation subsection.
   *
   * @param {string} recommendationId - Type of recommendation (subSectionCode)
   * @param {number} recommendationOrderNumber - Unique order number
   */
  function removeRecommendation(recommendationId, recommendationOrderNumber) {
    setScreeningRecommendationSectionData((prev) => {
      const currentSubSections = prev?.subSections || []

      const updatedSubSections = currentSubSections.filter(
        (section) =>
          !(
            section?.subSectionCode === recommendationId &&
            section?.orderNumber === recommendationOrderNumber
          ),
      )

      return {
        ...prev,
        subSections: updatedSubSections,
      }
    })
  }

  /**
   * Edits a specific recommendation's values.
   * Supports functional updater pattern like setState.
   *
   * @param {Function|Object} prevOrObject - Updater function or new values object
   * @param {string} recommendationId - Type of recommendation (subSectionCode)
   * @param {number} recommendationOrderNumber - Unique order number
   */
  function editRecommendation(prevOrObject, recommendationId, recommendationOrderNumber) {
    setScreeningRecommendationSectionData((prev) => {
      const currentSubSections = prev?.subSections || []

      const updatedSubSections = currentSubSections.map((subSection) => {
        // Find the matching subsection
        console.log({ subSection })
        if (
          subSection?.subSectionCode === recommendationId &&
          subSection?.orderNumber === recommendationOrderNumber
        ) {
          let updatedValues

          // Support functional updater pattern
          if (typeof prevOrObject === 'function') {
            updatedValues = prevOrObject(subSection.values || {})
          } else {
            updatedValues = prevOrObject
          }

          return {
            ...subSection,
            subSectionCode: recommendationId,
            subSectionLabel: subSection.title, // Retain existing label
            values: { ...updatedValues },
          }
        }

        return subSection
      })

      return {
        ...prev,
        subSections: updatedSubSections,
      }
    })
  }

  return {
    createRecommendation,
    removeRecommendation,
    editRecommendation,
  }
}
